import React, { useState, useEffect, useCallback } from "react";
import styles from "./ProspectList.module.sass";
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Filters from "../../components/Filters";
import Table from "./Table";
import Panel from "./Panel";
import Settings from "./Settings";
import Icon from "../../components/Icon";
import { useNavigate } from "react-router-dom";

// Import direct des fonctions du service
import { getProspectList } from "../../services/prospect.service";

const ProspectList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [selectedProspects, setSelectedProspects] = useState([]);

  // État pour gérer les résultats vides après une recherche
  const [noResults, setNoResults] = useState(false);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récents",
    selectedStatuses: []
  });
  
  // Charger les prospects avec gestion de la recherche et pagination
  const loadProspects = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // Construire les filtres
      let filterParts = [];
      
      // Filtre de recherche
      if (search.trim()) {
        filterParts.push(`(first_name ~ "${search}" || last_name ~ "${search}" || email ~ "${search}" || phone ~ "${search}")`);
      }
      
      // Filtre pour le statut du prospect (multi-sélection)
      if (filters.selectedStatuses && filters.selectedStatuses.length > 0) {
        // Construire un filtre OR pour les statuts sélectionnés
        const statusFilters = filters.selectedStatuses.map(s => `status="${s}"`).join(' || ');
        filterParts.push(`(${statusFilters})`);
      }
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : null;
      
      // Déterminer le tri
      let sortString = "-created"; // Par défaut : plus récents
      if (filters.sorting === "Plus anciens") {
        sortString = "+created";
      }

      // Options pour la requête
      const options = {
        page: currentPage,
        perPage: 10,
        sort: sortString,
        expand: "desired_formations,assigned_to"
      };

      // Ajouter le filtre si nécessaire
      if (filterString) {
        options.filter = filterString;
      }

      // Appel à l'API
      const result = await getProspectList(options);

      // Mise à jour de l'état avec les données reçues
      setProspects(result.items || []);
      setTotalPages(result.totalPages || 1);
      setNoResults(result.items.length === 0 && !!search);
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des prospects:", err);
        setError("Impossible de charger les prospects. Veuillez réessayer.");
        setProspects([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, filters]);
  
  useEffect(() => {
    loadProspects();
  }, [loadProspects]);

  // Nouvelle fonction pour gérer le changement de la recherche
  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setCurrentPage(1); // Réinitialiser à la page 1 lors d'une nouvelle recherche
    loadProspects();
  };

  // Gérer le changement de page
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Gérer la sélection d'un prospect
  const handleProspectSelect = (id) => {
    const prospect = prospects.find(prospect => prospect.id === id);
    if (prospect) {
      setSelectedProspect(prospect);
    }
  };

  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    console.log('Nouveaux filtres appliqués:', newFilters);
    setFilters(newFilters);
    setCurrentPage(1); // Remettre à la première page lors du changement de filtres
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Candidats"
        classTitle={cn("title-purple", styles.title)}
        classCardHead={styles.head}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={handleSearchChange}
              onSubmit={handleSubmit}
              placeholder="Rechercher par nom, email ou téléphone"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/prospects_student/create")}>Créer</button>
            <Filters
              className={styles.filters}
              title="Filtrer"
            >
              <Settings onFiltersChange={handleFiltersChange} />
            </Filters>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}
        
        {loading ? (
          <div className={styles.loading}>Chargement des candidats...</div>
        ) : (
          <>
            {noResults && (
              <div className={styles.empty}>
                Aucun candidat trouvé pour votre recherche "{search}".
              </div>
            )}
            {!noResults && prospects.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucun candidat disponible.
              </div>
            )}
            {prospects.length > 0 && (
              <>
                <div className={styles.row}>
                  <Table
                    className={styles.table}
                    items={prospects}
                    onProspectSelect={handleProspectSelect}
                    onSelectionChange={setSelectedProspects}
                  />
                </div>
                
                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
                    disabled={currentPage === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
                    disabled={currentPage === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </Card>
      {selectedProspects.length > 0 && (
        <Panel 
          selectedProspects={selectedProspects} 
          onDelete={(deletedIds) => {
            setProspects(prospects.filter(prospect => !deletedIds.includes(prospect.id)));
            setSelectedProspects([]);
          }}
        />
      )}
    </>
  );
};

export default ProspectList;
